import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Container, Loading, ErrorMessage } from '@/src/ui';
import { QuestionController } from './components/QuestionController';
import type { QtiQuestionDTO } from '@/src/lib';
import { apiRequest } from '@/src/services';
import { Constants } from '@/src/utils';
const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

const CreateEditQuestionPage: React.FC = () => {
    const [initialModel, setInitialModel] = useState<QtiQuestionDTO | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [searchParams] = useSearchParams();
    const viewMode = (searchParams.get('viewMode') as 'create' | 'edit') || 'create';
    const resourceId = searchParams.get('resourceId')
        ? Number(searchParams.get('resourceId'))
        : undefined;
    const urlQtm = searchParams.get('questionTypeMasterId')
        ? Number(searchParams.get('questionTypeMasterId'))
        : 1;

    const [questionTypeMasterId, setQuestionTypeMasterId] = useState(urlQtm || 1);

    useEffect(() => {
        document.title = viewMode === 'create' ? 'Create Question' : 'Edit Question';
    }, [viewMode]);

    useEffect(() => {
        if (viewMode === 'edit' && resourceId) {
            // Fetch the question model by resourceId
            (async function fetchQuestion() {
                setLoading(true);
                try {
                    const res = await apiRequest<QtiQuestionDTO>(
                        'get',
                        `/assessments/questions/${resourceId}`
                    );
                    setInitialModel(res.data);
                } catch {
                    setError('Failed to fetch question model');
                } finally {
                    setLoading(false);
                }
            })();
        } else {
            setInitialModel(undefined); // Reset for create mode
            setLoading(false);
        }
    }, [viewMode, resourceId]);

    // Question type options based on QuestionController
    const questionTypes = [
        { id: qtm.MULTIPLE_CHOICE, label: 'Multiple Choice' },
        { id: qtm.OPEN_TEXT, label: 'Open Text' },
        { id: qtm.MULTIPLE_SELECT, label: 'Multi-select' },
        { id: qtm.MATRIX, label: 'Matrix' },
        { id: qtm.HOTSPOT, label: 'Hotspot' },
        { id: qtm.DRAG_AND_DROP, label: 'Drag & Drop' },
        { id: qtm.STIMULUS, label: 'Stimulus' },
        // { id: 8, label: 'Segment' },
        { id: qtm.GRAPHING, label: 'Graphing' },
        // { id: 10, label: 'Dynamic' },
    ];

    const handleTypeChange = (id: number) => {
        if (questionTypeMasterId !== id) {
            const confirmed = window.confirm(
                'You will lose any unsaved progress if you switch question types. Continue?'
            );
            if (!confirmed) return;
        }
        setQuestionTypeMasterId(id);
    };

    return (
        <Container>
            <h1 className="page-header">
                {viewMode === 'create' ? 'Create Question' : 'Edit Question'}
            </h1>
            <br />

            <div className="mb-4 flex flex-wrap gap-2">
                {viewMode === 'create' &&
                    questionTypes.map((type) => (
                        <button
                            key={type.id}
                            type="button"
                            className={`px-3 py-1 rounded border ${questionTypeMasterId === type.id ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border-blue-600'} transition`}
                            onClick={() => handleTypeChange(type.id)}
                        >
                            {type.label}
                        </button>
                    ))}
            </div>

            <Loading visible={loading} size="lg" />
            <ErrorMessage error={error} />

            <br />

            {!loading && (
                <div className="user-min-font-size">
                    <QuestionController
                        questionType={questionTypeMasterId}
                        initialModel={initialModel}
                    />
                </div>
            )}
        </Container>
    );
};

export default CreateEditQuestionPage;
